/**
 * Paragraph writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const paragraphWritingFormatsPack: ToilePack = {
    slug: 'paragraph-writing-formats',
    category: 'classroom',
    name: 'Paragraph writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'burger', label: 'Hamburger', subject: 'a hamburger diagram labelled "Topic Sentence", "Detail 1", "Detail 2", "Detail 3", "Closing Sentence"'},
        {key: 'topic', label: 'Topic sentence', subject: 'a ruled page with the first line highlighted and labelled "Topic sentence"'},
        {key: 'checklist', label: 'Checklist', subject: 'a paragraph format with a five-item checklist beside the lines'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Paragraph Writing Format: Hamburger, Topic, Checklist',
        metaDescription: 'Paragraph writing format on one labelled page: a hamburger paragraph diagram, a topic sentence page and a checklist, in three styles.',
        h1: 'Paragraph writing format: hamburger paragraph, topic sentence, checklist',
        tagline: 'Nine labelled pages, three paragraph layouts in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing how a paragraph is built, with each part labelled. The hamburger diagram has a topic sentence, three details and a closing sentence. The topic-sentence page highlights the first line of the paragraph. The checklist page has five items beside the ruled lines. Ask for another paragraph layout and the same labelled format is asked for.',
        useCases: [
            'a classroom wall chart for paragraph writing',
            'a reference in a writing notebook',
            'a handout before a paragraph exercise',
            "a quick checklist for a writer's own review",
        ],
        faq: [
            {q: 'What is on the hamburger diagram?', a: 'A topic sentence, three detail sentences and a closing sentence, each labelled.'},
            {q: 'What is on the checklist?', a: 'Five items set beside the ruled lines of a paragraph.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a different paragraph layout?', a: 'Yes. Add a direction for another layout and the labelled format is asked for with it.'},
        ],
    },
}
